// src/components/subPageContentHolder/SubPageInnerContentHolder.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { arrowRightThinSVG, filterSVG } from '../../lib/allCustomSVGs';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import SquareGrayButton from '../../ui/buttons/SquareGrayButton';
import '../../styles/components/subPageContentHolder/subPageInnerContentHolder.scss';
import { useTranslation } from 'react-i18next';
import DashboardFilterTime from './DashboardFilterTime';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import DashboardFilterEvent from './DashboardFilterEvent';
import TaraMCPConnections from './TaraMCPConnections';

type MainButtonType =
  | 'danger'
  | 'warning'
  | 'success'
  | 'light'
  | 'filter'
  | 'delete'
  | 'white'
  | undefined;

interface Props {
  title: string;
  backgroundColor?: string;
  padding?: string;
  subTitle?: string;
  backBtnLink?: string;
  backBtnAction?: () => void;
  children: React.ReactNode;

  mainBtnIcon?: any;
  mainBtnLoading?: boolean;
  mainBtnTitle?: string;
  mainBtnLink?: string;
  mainButtonType?: MainButtonType;
  mainBtnAction?: () => void;
  mainBtnDisabled?: boolean;
  isMainBtnLinkExternal?: boolean;

  secondaryBtnIcon?: any;
  secondaryBtnLoading?: boolean;
  secondaryBtnTitle?: string;
  secondaryBtnLink?: string;
  secondaryButtonType?: MainButtonType;
  secondaryBtnDisabled?: boolean;
  secondaryBtnAction?: () => void;

  tertiaryBtnIcon?: any;
  tertiaryBtnLoading?: boolean;
  tertiaryBtnTitle?: string;
  tertiaryBtnLink?: string;
  tertiaryButtonType?: MainButtonType;
  tertiaryBtnDisabled?: boolean;
  tertiaryBtnAction?: () => void;

  hasFilter?: boolean;
  filterHandler?: () => void;
  isFilterActive?: boolean;
  dashboardEventFilterHandler?: (value: string) => void;
  eventFilter?: string;
  dashboardPeriodFilterHandler?: (value: string) => void;
  timeFilter?: string;
  setCustomPeriodFilterDateRange?: (value: {
    start_date: string | undefined;
    end_date: string | undefined;
  }) => void;
  customPeriodFilterDateRange?: {
    start_date: string | undefined;
    end_date: string | undefined;
  };
  isTaraMCP?: boolean;
}

const SubPageInnerContentHolder = ({
  children,
  title,
  backgroundColor,
  padding,
  subTitle,
  backBtnLink,
  backBtnAction,

  mainBtnIcon,
  mainBtnTitle,
  mainBtnLink,
  mainBtnLoading,
  mainButtonType,
  mainBtnAction,
  mainBtnDisabled,
  isMainBtnLinkExternal,

  secondaryBtnIcon,
  secondaryBtnTitle,
  secondaryBtnLink,
  secondaryBtnLoading,
  secondaryButtonType,
  secondaryBtnDisabled,
  secondaryBtnAction,

  tertiaryBtnIcon,
  tertiaryBtnTitle,
  tertiaryBtnLink,
  tertiaryBtnLoading,
  tertiaryButtonType,
  tertiaryBtnDisabled,
  tertiaryBtnAction,

  hasFilter,
  isFilterActive,
  filterHandler,
  dashboardEventFilterHandler,
  eventFilter,
  dashboardPeriodFilterHandler,
  timeFilter,
  setCustomPeriodFilterDateRange,
  customPeriodFilterDateRange,
  isTaraMCP,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSidebarMinimized = useSelector(
    (state: RootState) => state.UI.isSidebarMinimized
  );

  return (
    <div className='subPageInnerContentHolder'>
      <div
        style={{
          backgroundColor: backgroundColor
            ? backgroundColor
            : 'var(--containers-background-color)',
        }}
        className='subPageInnerContentHolder__content'
      >
        <div
          id='tara-header-bar' // <-- added id for App.tsx hover logic
          data-is-sidebar-minimized={isSidebarMinimized?.toString()}
          className='subPageInnerContentHolder__content--top'
        >
          <div className='subPageInnerContentHolder__content--top-right'>
            {backBtnLink && (
              <div className='subPageInnerContentHolder__content--top-right-button'>
                <SquareGrayButton
                  onClick={(e) => {
                    e.preventDefault();
                    backBtnAction && backBtnAction();
                    navigate(`${backBtnLink}`);
                  }}
                  type={'button'}
                  icon={arrowRightThinSVG}
                  normal={true}
                  classes='rotate-180-Deg squareGrayButton-mobile-view'
                />
              </div>
            )}
            <div className='subPageInnerContentHolder__content--top-right-title'>
              <h3>{title}</h3>
              {subTitle && <span>{subTitle}</span>}
            </div>
          </div>

          <div className='subPageInnerContentHolder__content--top-buttons'>
            <div className='subPageInnerContentHolder__content--top-buttons-backBtn'>
              {backBtnLink && (
                <SquareGrayButton
                  onClick={(e) => {
                    e.preventDefault();
                    backBtnAction && backBtnAction();
                    navigate(`${backBtnLink}`);
                  }}
                  type={'button'}
                  value={t('GoBack')}
                  icon={arrowRightThinSVG}
                  normal={true}
                  classes='rotate-180-Deg'
                />
              )}
            </div>

            {dashboardEventFilterHandler && eventFilter ? (
              <DashboardFilterEvent
                dashboardEventFilterHandler={dashboardEventFilterHandler}
                eventFilter={eventFilter}
              />
            ) : null}

            {isTaraMCP ? <TaraMCPConnections /> : null}

            {dashboardPeriodFilterHandler &&
            timeFilter &&
            setCustomPeriodFilterDateRange &&
            customPeriodFilterDateRange ? (
              <DashboardFilterTime
                dashboardPeriodFilterHandler={dashboardPeriodFilterHandler}
                timeFilter={timeFilter}
                setCustomPeriodFilterDateRange={setCustomPeriodFilterDateRange}
                customPeriodFilterDateRange={customPeriodFilterDateRange}
              />
            ) : null}

            {secondaryBtnTitle && (
              <MainSquareButton
                onClick={(e) => {
                  e.preventDefault();
                  if (secondaryBtnLink) {
                    navigate(`${secondaryBtnLink}`);
                  }
                  if (secondaryBtnAction) {
                    secondaryBtnAction();
                  }
                }}
                loading={secondaryBtnLoading}
                type={'button'}
                buttonType={secondaryButtonType}
                value={`${secondaryBtnTitle}`}
                icon={secondaryBtnIcon}
                normal={true}
                disabled={secondaryBtnDisabled}
              />
            )}

            {tertiaryBtnTitle && (
              <MainSquareButton
                onClick={(e) => {
                  e.preventDefault();
                  if (tertiaryBtnLink) {
                    navigate(`${tertiaryBtnLink}`);
                  }
                  if (tertiaryBtnAction) {
                    tertiaryBtnAction();
                  }
                }}
                loading={tertiaryBtnLoading}
                type={'button'}
                buttonType={tertiaryButtonType}
                value={`${tertiaryBtnTitle}`}
                icon={tertiaryBtnIcon}
                normal={true}
                disabled={tertiaryBtnDisabled}
              />
            )}

            {mainBtnTitle && (
              <MainSquareButton
                onClick={(e) => {
                  e.preventDefault();
                  if (mainBtnLink && !isMainBtnLinkExternal) {
                    navigate(`${mainBtnLink}`);
                  } else if (mainBtnLink && isMainBtnLinkExternal) {
                    window.open(mainBtnLink, '_blank');
                  }
                  if (mainBtnAction) {
                    mainBtnAction();
                  }
                }}
                loading={mainBtnLoading}
                disabled={mainBtnDisabled}
                type={'button'}
                buttonType={mainButtonType}
                value={`${mainBtnTitle}`}
                icon={mainBtnIcon}
                normal={true}
              />
            )}

            {hasFilter && (
              <MainSquareButton
                onClick={(e) => {
                  e.preventDefault();
                  if (filterHandler) {
                    filterHandler();
                  }
                }}
                type={'button'}
                value={t('Filter')}
                icon={filterSVG}
                normal={true}
                buttonType={isFilterActive ? 'filter' : 'light'}
              />
            )}
          </div>
        </div>

        <div
          style={{ padding: padding ? padding : '2rem 2rem' }}
          className='subPageInnerContentHolder__content--bottom'
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default SubPageInnerContentHolder;
